import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import {
  searchWhatsappMedidor,
  createWhatsappReading,
  getTodayWhatsappReadings,
} from "../services/api";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  Search,
  LogOut,
  Upload,
  X,
  CheckSquare,
  Square,
  FileSpreadsheet,
} from "lucide-react";

// ── CSV helpers ────────────────────────────────────────────────────────────────

function normalizeHeader(h) {
  return h.toUpperCase().replace(/\s+/g, "_").replace(/[^A-Z0-9_]/g, "");
}

function splitLine(line, sep) {
  if (sep !== ",") return line.split(sep).map((s) => s.replace(/^"|"$/g, "").trim());
  // RFC 4180: handle quoted fields that may contain commas
  const cells = [];
  let cur = "";
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
      else inQ = !inQ;
    } else if (c === "," && !inQ) {
      cells.push(cur.trim());
      cur = "";
    } else {
      cur += c;
    }
  }
  cells.push(cur.trim());
  return cells;
}

function parseCsvText(text) {
  const clean = text.charCodeAt(0) === 0xFEFF ? text.slice(1) : text;
  const lines = clean.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return [];
  const first = lines[0];
  const sep = first.includes("\t") ? "\t" : first.includes(";") ? ";" : ",";
  const headers = splitLine(first, sep).map((h) => normalizeHeader(h));
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = splitLine(lines[i], sep);
    if (!cells.some(Boolean)) continue;
    const row = {};
    headers.forEach((h, idx) => { row[h] = cells[idx] ?? ""; });
    if (row["NRO_MEDIDOR"]?.trim()) rows.push(row);
  }
  return rows;
}

function parseEnviado(val) {
  if (!val) return false;
  const v = String(val).toLowerCase().trim();
  return v === "si" || v === "sí" || v === "1" || v === "true" || v === "yes";
}

// Converts a raw CSV row (uppercase keys) to the shape used in the form / DB.
// Fields that aren't present in the CSV are returned as null so they don't
// override values that were already loaded from a previous reading.
function mapCsvRow(row) {
  const get = (col) => (row[col] !== undefined && row[col] !== "" ? row[col] : null);
  return {
    turno:              get("TURNO"),
    localidad_ruta:     get("LOCALIDAD_RUTA"),
    ruta:               get("RUTA"),
    calle:              get("CALLE"),
    nro_puerta:         get("NRO_PUERTA"),
    datos_dom_servicio: get("DATOS_DOM_SERVICIO"),
    poliza:             get("POLIZA"),
    lecturista:         get("LECTURISTA"),
    telefono1:          get("TELEFONO_1"),
    telefono2:          get("TELEFONO_2"),
    telefono3:          get("TELEFONO_3"),
    // null when column is absent (don't touch the checkbox in that case)
    enviado_naturgy: row["ENVIADO_NATURGY"] !== undefined && row["ENVIADO_NATURGY"] !== ""
      ? parseEnviado(row["ENVIADO_NATURGY"])
      : null,
  };
}

// ── IndexedDB cache for CSV ────────────────────────────────────────────────────

const IDB_NAME  = "wpp_padron";
const IDB_STORE = "csv";
const CSV_VER_KEY = "wpp_csv_version_v2"; // v2: parser con RFC 4180 + BOM strip

function openIdb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = (e) => e.target.result.createObjectStore(IDB_STORE);
    req.onsuccess  = (e) => resolve(e.target.result);
    req.onerror    = ()  => reject(req.error);
  });
}

async function saveCsvToIdb(text) {
  const db = await openIdb();
  await new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, "readwrite");
    tx.objectStore(IDB_STORE).put(text, "current");
    tx.oncomplete = resolve;
    tx.onerror    = () => reject(tx.error);
  });
  db.close();
}

async function loadCsvFromIdb() {
  const db = await openIdb();
  const text = await new Promise((resolve, reject) => {
    const tx  = db.transaction(IDB_STORE, "readonly");
    const req = tx.objectStore(IDB_STORE).get("current");
    req.onsuccess = () => resolve(req.result || null);
    req.onerror   = () => reject(req.error);
  });
  db.close();
  return text;
}

// Normalización consistente: colapsa espacios múltiples, trim, lowercase
function norm(s) {
  return (s ?? "").replace(/\s+/g, " ").trim().toLowerCase();
}

function addrKey(calle, nroPuerta) {
  return norm(calle) + "|" + norm(nroPuerta);
}

function buildIndex(rows) {
  const idx = new Map();
  const addrIdx = new Map(); // addrKey(calle, nro_puerta) → [nro_medidor original, ...]
  for (const row of rows) {
    const rawMedidor = row["NRO_MEDIDOR"] ?? "";
    const key = norm(rawMedidor);
    if (!key) continue;
    const mapped = mapCsvRow(row);
    idx.set(key, mapped);
    if (mapped.calle && mapped.nro_puerta) {
      const k = addrKey(mapped.calle, mapped.nro_puerta);
      if (!addrIdx.has(k)) addrIdx.set(k, []);
      addrIdx.get(k).push(rawMedidor.trim());
    }
  }
  return { idx, addrIdx };
}


// ── Component ─────────────────────────────────────────────────────────────────

export default function WhatsappPage() {
  const { user, logout } = useAuth();

  // Form — meter search
  const [medidorInput, setMedidorInput] = useState("");
  const [searching, setSearching] = useState(false);
  const [existingRecord, setExistingRecord] = useState(null);
  const [medidorConfirmed, setMedidorConfirmed] = useState(false);

  // Form — editable fields
  const [telefonos, setTelefonos] = useState([""]);
  const [nombreContacto, setNombreContacto] = useState("");
  const [lectura, setLectura] = useState("");
  const [turno, setTurno] = useState("");
  const [enviadoNaturgy, setEnviadoNaturgy] = useState(false);
  const [observacion, setObservacion] = useState("");

  // Form — photos (multiple)
  const [photoFiles, setPhotoFiles] = useState([]);             // new File[] to upload
  const [photoPreviews, setPhotoPreviews] = useState([]);       // [{ url, isExisting }]
  const [isDragging, setIsDragging] = useState(false);
  const fileRef = useRef();

  // Form — submit
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // History
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  // CSV en memoria
  const [csvCount, setCsvCount] = useState(0);
  const [csvParsing, setCsvParsing] = useState(false);
  const csvIndexRef = useRef(new Map());
  const addressIndexRef = useRef(new Map()); // "calle|nro_puerta" → [nro_medidor]
  // Datos del padrón encontrados en el CSV (null si no hay match)
  const [csvInfo, setCsvInfo] = useState(null);
  // Medidores en la misma dirección (incluye el buscado); vacío si solo hay uno
  const [addressMatches, setAddressMatches] = useState([]);

  const hasPhoto = photoPreviews.length > 0;
  const formUnlocked = medidorConfirmed;

  useEffect(() => {
    loadHistory();
    initCsv();
  }, []);

  async function loadHistory() {
    setLoadingHistory(true);
    try {
      setHistory(await getTodayWhatsappReadings());
    } catch {
      setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  }

  // ── CSV auto-load desde el host ───────────────────────────────────────────
  async function initCsv() {
    setCsvParsing(true);
    try {
      // Verificar versión en el servidor (HEAD request)
      const head = await fetch("/padron.csv", { method: "HEAD" }).catch(() => null);
      if (!head || !head.ok) return; // archivo no disponible todavía

      const serverVersion =
        head.headers.get("etag") || head.headers.get("last-modified") || "";
      const cachedVersion = localStorage.getItem(CSV_VER_KEY);

      if (cachedVersion && cachedVersion === serverVersion) {
        // Usar caché de IndexedDB
        const cached = await loadCsvFromIdb().catch(() => null);
        if (cached) {
          const rows = parseCsvText(cached);
          if (rows.length > 0) {
            const { idx, addrIdx } = buildIndex(rows);
            csvIndexRef.current = idx;
            addressIndexRef.current = addrIdx;
            console.log("[WPP] CSV cargado (IDB):", rows.length, "medidores:", idx.size, "direcciones:", addrIdx.size);
            setCsvCount(rows.length);
            return;
          }
        }
      }

      // Descargar CSV completo del host
      const res = await fetch("/padron.csv");
      if (!res.ok) return;
      const text = await res.text();
      const rows = parseCsvText(text);
      if (rows.length > 0) {
        const { idx, addrIdx } = buildIndex(rows);
        csvIndexRef.current = idx;
        addressIndexRef.current = addrIdx;
        console.log("[WPP] CSV descargado (server):", rows.length, "medidores:", idx.size, "direcciones:", addrIdx.size);
        setCsvCount(rows.length);
        saveCsvToIdb(text).catch(() => {});
        if (serverVersion) localStorage.setItem(CSV_VER_KEY, serverVersion);
      }
    } catch {
      // silencioso — el CSV es best-effort
    } finally {
      setCsvParsing(false);
    }
  }

  // ── Meter search ───────────────────────────────────────────────────────────
  async function handleSearch(e) {
    e?.preventDefault();
    const q = medidorInput.trim();
    if (!q) return;
    setSearching(true);
    setError("");
    try {
      const found = await searchWhatsappMedidor(q);
      setExistingRecord(found);
      setMedidorConfirmed(true);

      // Datos de lectura previa en whatsapp_readings
      if (found) {
        setTelefonos(
          found.telefonos?.length
            ? found.telefonos
            : [found.telefono, found.telefono2, found.telefono3].filter(Boolean).length
              ? [found.telefono, found.telefono2, found.telefono3].filter(Boolean)
              : [""]
        );
        setNombreContacto(found.nombre_contacto || "");
        setLectura(found.lectura || "");
        const existingUrls = found.photo_urls?.length
          ? found.photo_urls
          : found.photo_url
          ? [found.photo_url]
          : [];
        if (existingUrls.length > 0) {
          setPhotoFiles([]);
          setPhotoPreviews(existingUrls.map((url) => ({ url, isExisting: true })));
        }
      }

      // CSV en memoria tiene prioridad para teléfonos / turno / enviado
      const csvKey = norm(q);
      const csvRow = csvIndexRef.current.get(csvKey);
      console.log("[WPP] buscar CSV:", { key: csvKey, found: !!csvRow, indexSize: csvIndexRef.current.size });
      if (csvRow) {
        setCsvInfo(csvRow);
        {
          const csvPhones = [csvRow.telefono1, csvRow.telefono2, csvRow.telefono3].filter(Boolean);
          if (csvPhones.length) setTelefonos(csvPhones);
        }
        if (csvRow.turno)                    setTurno(String(csvRow.turno));
        if (csvRow.enviado_naturgy !== null) setEnviadoNaturgy(csvRow.enviado_naturgy);

        // Buscar otros medidores con la misma calle + nro_puerta
        if (csvRow.calle && csvRow.nro_puerta) {
          const k = addrKey(csvRow.calle, csvRow.nro_puerta);
          const matches = addressIndexRef.current.get(k) || [];
          console.log("[WPP] addr:", { key: k, matches: matches.length, list: matches });
          setAddressMatches(matches.length > 1 ? matches : []);
        } else {
          setAddressMatches([]);
        }
      } else {
        setCsvInfo(null);
        setAddressMatches([]);
      }
    } catch {
      setError("Error al buscar el medidor.");
    } finally {
      setSearching(false);
    }
  }

  // ── Photo handlers ─────────────────────────────────────────────────────────
  function addImages(fileList) {
    const valid = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
    if (!valid.length) return;
    setPhotoFiles((prev) => [...prev, ...valid]);
    setPhotoPreviews((prev) => [
      ...prev,
      ...valid.map((f) => ({ url: URL.createObjectURL(f), isExisting: false })),
    ]);
    setError("");
  }
  function handlePhotoChange(e) {
    if (e.target.files?.length) addImages(e.target.files);
    e.target.value = "";
  }
  function handleDragOver(e) { e.preventDefault(); setIsDragging(true); }
  function handleDragLeave(e) {
    if (!e.currentTarget.contains(e.relatedTarget)) setIsDragging(false);
  }
  async function handleDrop(e) {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files?.length > 0) return addImages(e.dataTransfer.files);
    const url =
      e.dataTransfer.getData("text/uri-list") ||
      e.dataTransfer.getData("text/plain");
    if (url && /^https?:\/\//.test(url.trim())) {
      try {
        const res = await fetch(url.trim());
        const blob = await res.blob();
        if (!blob.type.startsWith("image/")) return setError("El archivo no es una imagen.");
        const ext = blob.type.split("/")[1] || "jpg";
        addImages([new File([blob], `imagen.${ext}`, { type: blob.type })]);
      } catch {
        setError("No se pudo cargar la imagen desde esa URL. Intentá guardarla primero.");
      }
    }
  }
  function removePhoto(index) {
    const p = photoPreviews[index];
    if (!p.isExisting) {
      URL.revokeObjectURL(p.url);
      const fileIdx = photoPreviews.slice(0, index).filter((x) => !x.isExisting).length;
      setPhotoFiles((prev) => prev.filter((_, i) => i !== fileIdx));
    }
    setPhotoPreviews((prev) => prev.filter((_, i) => i !== index));
  }

  // ── Reset ──────────────────────────────────────────────────────────────────
  function resetAll() {
    setMedidorInput("");
    setMedidorConfirmed(false);
    setExistingRecord(null);
    setTelefonos([""]);
    setNombreContacto(""); setLectura(""); setTurno("");
    setEnviadoNaturgy(false); setObservacion("");
    setCsvInfo(null); setAddressMatches([]);
    setPhotoFiles([]); setPhotoPreviews([]);
    if (fileRef.current) fileRef.current.value = "";
  }

  // ── Submit ─────────────────────────────────────────────────────────────────
  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (!medidorInput.trim())        return setError("El medidor es obligatorio.");
    if (!telefonos[0]?.trim())       return setError("El teléfono es obligatorio.");

    // Targets: todos los medidores de la misma dirección, o solo el buscado
    const targets = addressMatches.length > 1 ? addressMatches : [medidorInput.trim()];

    setSaving(true);
    try {
      // Fotos ya en Cloudinary que el usuario conservó
      const keptUrls = photoPreviews.filter((p) => p.isExisting).map((p) => p.url);

      // URLs finales a reutilizar en los registros 2…N (se llena tras el primer POST)
      let sharedPhotoUrls = null;

      const failed = [];

      for (let i = 0; i < targets.length; i++) {
        const targetMedidor = targets[i];
        // Cada medidor usa sus propios datos del CSV (turno, poliza, etc.)
        const row = csvIndexRef.current.get(norm(targetMedidor)) ?? csvInfo;

        const fd = new FormData();
        fd.append("medidor",          targetMedidor);
        // Info de contacto — igual para todos (el operario la completó)
        const validPhones = telefonos.map((t) => t.trim()).filter(Boolean);
        fd.append("telefonos", JSON.stringify(validPhones));
        fd.append("nombre_contacto",  nombreContacto.trim());
        fd.append("lectura",          lectura.trim());
        fd.append("enviado_naturgy",  enviadoNaturgy);
        fd.append("observacion",      observacion.trim());
        // Turno y metadatos del CSV son propios de cada medidor
        fd.append("turno", row?.turno != null ? String(row.turno) : turno);
        if (row) {
          if (row.localidad_ruta)     fd.append("localidad_ruta",     row.localidad_ruta);
          if (row.ruta)               fd.append("ruta",               row.ruta);
          if (row.calle)              fd.append("calle",              row.calle);
          if (row.nro_puerta)         fd.append("nro_puerta",         row.nro_puerta);
          if (row.datos_dom_servicio) fd.append("datos_dom_servicio", row.datos_dom_servicio);
          if (row.poliza)             fd.append("poliza",             row.poliza);
        }
        // Fotos: solo se suben a Cloudinary en la primera iteración
        if (i === 0) {
          photoFiles.forEach((f) => fd.append("photos", f));
          if (keptUrls.length) fd.append("existing_photo_urls", JSON.stringify(keptUrls));
        } else {
          const reuse = sharedPhotoUrls ?? keptUrls;
          if (reuse.length) fd.append("existing_photo_urls", JSON.stringify(reuse));
        }

        try {
          const result = await createWhatsappReading(fd);
          // Guardar las URLs definitivas (incluyen las recién subidas) para reutilizar
          if (i === 0) {
            sharedPhotoUrls = result.photo_urls?.length
              ? result.photo_urls
              : keptUrls;
          }
        } catch (err) {
          failed.push(targetMedidor);
          if (i === 0) break; // Si falló la subida de fotos, no tiene sentido continuar
        }
      }

      const saved = targets.length - failed.length;
      if (saved === 0) {
        setError("No se pudo guardar ningún registro.");
      } else {
        if (failed.length > 0)
          setError(`No se pudieron guardar: ${failed.join(", ")}`);
        setSuccessMsg(saved > 1 ? `Guardado para ${saved} medidores` : "Guardado correctamente");
        setTimeout(() => setSuccessMsg(""), 4000);
        resetAll();
        loadHistory();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  const now = new Date();

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={s.root}>
      <div style={s.header}>
        <div style={s.headerLeft} />
        <button style={s.logoutBtn} onClick={logout}>
          <LogOut size={14} style={{ marginRight: 6 }} />
          Cerrar sesión
        </button>
      </div>

      <div style={s.page}>
        <h1 style={s.pageTitle}>Carga WhatsApp</h1>

        {/* ── Card padrón ── */}
        <div style={s.padronCard}>
          <div style={s.padronCardLeft}>
            <FileSpreadsheet
              size={18}
              color={csvCount > 0 ? "#16a34a" : "#94a3b8"}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={s.padronTitle}>Padrón CSV</span>
              {csvParsing ? (
                <span style={s.padronStatusEmpty}>Cargando padrón...</span>
              ) : csvCount > 0 ? (
                <span style={s.padronStatusOk}>
                  {csvCount.toLocaleString()} registros disponibles
                </span>
              ) : (
                <span style={s.padronStatusEmpty}>
                  Padrón no disponible
                </span>
              )}
            </div>
          </div>
          {csvCount === 0 && !csvParsing && (
            <button
              type="button"
              style={s.btnPadronImport}
              onClick={initCsv}
            >
              <Upload size={13} />
              Reintentar
            </button>
          )}
        </div>

        {/* ── Formulario ── */}
        <form onSubmit={handleSubmit}>
          <div style={s.card}>
            {/* Medidor */}
            <p style={s.sectionLabel}>Medidor</p>
            <div style={s.medidorRow}>
              <input
                style={{ ...s.input, flex: 1 }}
                placeholder="Ingresá el código de medidor..."
                value={medidorInput}
                onChange={(e) => {
                  setMedidorInput(e.target.value);
                  setMedidorConfirmed(false);
                  setExistingRecord(null);
                  setCsvInfo(null);
                  setAddressMatches([]);
                }}
                onKeyDown={(e) =>
                  e.key === "Enter" && (e.preventDefault(), handleSearch())
                }
                autoComplete="off"
                autoFocus
              />
              <button
                type="button"
                style={{ ...s.btnPrimary, display: "flex", alignItems: "center", gap: 6 }}
                onClick={handleSearch}
                disabled={searching || !medidorInput.trim()}
              >
                <Search size={14} />
                {searching ? "Buscando..." : "Buscar"}
              </button>
            </div>

            {medidorConfirmed && (
              <div style={existingRecord ? s.bannerFound : s.bannerNew}>
                {existingRecord
                  ? `✓ Encontrado — última carga: ${format(new Date(existingRecord.ultima_carga_wpp), "dd/MM/yyyy HH:mm", { locale: es })} · Podés cambiar la foto y el valor`
                  : "Medidor nuevo — se creará un registro"}
              </div>
            )}

            {addressMatches.length > 1 && (
              <div style={s.addressBanner}>
                <span style={s.addressBannerTitle}>
                  {addressMatches.length} medidores en esta dirección — se guardará uno por uno
                </span>
                <div style={s.addressChips}>
                  {addressMatches.map((m) => (
                    <span
                      key={m}
                      style={
                        m.toLowerCase() === medidorInput.trim().toLowerCase()
                          ? s.chipCurrent
                          : s.chipOther
                      }
                    >
                      {m}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Info del padrón */}
            {csvInfo && (
              <div style={s.padronInfoBlock}>
                <p style={{ ...s.sectionLabel, color: "#15803d", marginBottom: 8 }}>
                  Datos del padrón
                </p>
                <div style={s.padronInfoGrid}>
                  {[
                    ["Turno",          csvInfo.turno != null ? String(csvInfo.turno) : null],
                    ["Localidad/Ruta", csvInfo.localidad_ruta],
                    ["Ruta",           csvInfo.ruta],
                    ["Calle",          csvInfo.calle],
                    ["Nro. Puerta",    csvInfo.nro_puerta],
                    ["Dom. Servicio",  csvInfo.datos_dom_servicio],
                    ["Póliza",         csvInfo.poliza],
                    ["Lecturista",     csvInfo.lecturista],
                  ]
                    .filter(([, v]) => v)
                    .map(([label, val]) => (
                      <div key={label} style={s.padronInfoCell}>
                        <span style={s.padronInfoLabel}>{label}</span>
                        <span style={s.padronInfoVal}>{val}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}

            <div style={s.divider} />

            {/* Fotos */}
            <div
              style={{
                opacity: medidorConfirmed ? 1 : 0.4,
                pointerEvents: medidorConfirmed ? "auto" : "none",
                transition: "opacity .2s",
              }}
              onDragOver={handleDragOver}
              onDragEnter={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <p style={s.sectionLabel}>
                Fotos
                <span style={s.required}> * al menos una requerida</span>
              </p>

              {photoPreviews.length > 0 && (
                <div style={s.photoGrid}>
                  {photoPreviews.map((p, i) => (
                    <div key={i} style={s.photoWrap}>
                      <img src={p.url} alt="" style={s.photoGridImg} />
                      <button type="button" style={s.removeBtn} onClick={() => removePhoto(i)}>
                        <X size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <button
                type="button"
                style={{
                  ...s.uploadArea,
                  ...(photoPreviews.length > 0 ? s.uploadAreaSmall : {}),
                  borderColor: isDragging ? "#D4620D" : "#e2e8f0",
                  background:  isDragging ? "#fff7ed" : "#f8fafc",
                  transform:   isDragging ? "scale(1.01)" : "scale(1)",
                  transition:  "border-color .15s, background .15s, transform .15s",
                }}
                onClick={() => fileRef.current.click()}
              >
                <Upload
                  size={photoPreviews.length > 0 ? 16 : 22}
                  color={isDragging ? "#D4620D" : "#94a3b8"}
                />
                <span style={{ ...s.uploadText, color: isDragging ? "#D4620D" : "#64748b" }}>
                  {isDragging
                    ? "Soltá las imágenes aquí"
                    : photoPreviews.length > 0
                    ? "Agregar más fotos"
                    : "Seleccionar fotos o arrastrá desde otra pestaña"}
                </span>
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                onChange={handlePhotoChange}
              />
            </div>

            <div style={s.divider} />

            {/* Campos */}
            <div
              style={{
                opacity: formUnlocked ? 1 : 0.35,
                pointerEvents: formUnlocked ? "auto" : "none",
                transition: "opacity .25s",
              }}
            >

              {/* Teléfonos */}
              <div style={{ marginBottom: 12 }}>
                <label style={s.fieldLabel}>Teléfonos *</label>
                {telefonos.map((t, i) => (
                  <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                    <input
                      className={i === 0 ? "wsp-i" : undefined}
                      style={{ ...s.input, flex: 1, marginBottom: 0 }}
                      type="tel"
                      placeholder={i === 0 ? "Ej: 1122334455" : "Otro teléfono"}
                      value={t}
                      onChange={(e) => {
                        const next = [...telefonos];
                        next[i] = e.target.value;
                        setTelefonos(next);
                      }}
                    />
                    {i > 0 && (
                      <button
                        type="button"
                        onClick={() => setTelefonos(telefonos.filter((_, j) => j !== i))}
                        style={s.removePhoneBtn}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setTelefonos([...telefonos, ""])}
                  style={s.addPhoneBtn}
                >
                  + Agregar teléfono
                </button>
              </div>

              {/* Otros */}
              <div style={{ ...s.grid3, marginTop: 12 }}>
                <div>
                  <label style={s.fieldLabel}>Nombre contacto</label>
                  <input
                    style={s.input}
                    placeholder="Opcional"
                    value={nombreContacto}
                    onChange={(e) => setNombreContacto(e.target.value)}
                  />
                </div>
                <div>
                  <label style={s.fieldLabel}>Lectura</label>
                  <input
                    style={s.input}
                    placeholder="Opcional"
                    value={lectura}
                    onChange={(e) => setLectura(e.target.value)}
                  />
                </div>
                <div>
                  <label style={s.fieldLabel}>Turno</label>
                  <input
                    style={s.input}
                    placeholder="Opcional"
                    type="number"
                    min="0"
                    value={turno}
                    onChange={(e) => setTurno(e.target.value)}
                  />
                </div>
              </div>

              {/* Naturgy */}
              <div style={s.checkRow} onClick={() => setEnviadoNaturgy((v) => !v)}>
                {enviadoNaturgy
                  ? <CheckSquare size={18} color="#D4620D" />
                  : <Square size={18} color="#94a3b8" />}
                <span style={s.checkLabel}>Enviado a Naturgy</span>
              </div>

              {/* Observación */}
              <div style={{ marginTop: 14 }}>
                <label style={s.fieldLabel}>Observación</label>
                <textarea
                  style={s.textarea}
                  placeholder="Opcional"
                  rows={2}
                  value={observacion}
                  onChange={(e) => setObservacion(e.target.value)}
                />
              </div>

              {/* Info automática */}
              <div style={s.autoGrid}>
                {[
                  { label: "Fecha",    val: format(now, "dd/MM/yyyy", { locale: es }) },
                  { label: "Hora",     val: format(now, "HH:mm",      { locale: es }) },
                  {
                    label: "Última carga WPP",
                    val: existingRecord
                      ? format(new Date(existingRecord.ultima_carga_wpp), "dd/MM/yy HH:mm", { locale: es })
                      : "—",
                  },
                  { label: "Usuario",  val: user?.name || user?.email || "—" },
                ].map(({ label, val }) => (
                  <div key={label} style={s.autoCell}>
                    <span style={s.autoCellLabel}>{label}</span>
                    <span style={s.autoCellVal}>{val}</span>
                  </div>
                ))}
              </div>
            </div>

            {error      && <div style={s.errorBox}>{error}</div>}
            {successMsg && <div style={s.successBox}>{successMsg}</div>}

            <button
              type="submit"
              style={{
                ...s.btnSave,
                opacity: formUnlocked ? 1 : 0.35,
                cursor:  formUnlocked ? "pointer" : "not-allowed",
                marginTop: 16,
              }}
              disabled={!formUnlocked || saving}
            >
              {saving ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </form>

        {/* ── Historial del día ── */}
        <div style={s.card}>
          <div style={s.historyHeader}>
            <h2 style={s.historyTitle}>
              Historial de hoy
              <span style={s.historyBadge}>{history.length}</span>
            </h2>
          </div>

          {loadingHistory ? (
            <div style={s.empty}>Cargando...</div>
          ) : history.length === 0 ? (
            <div style={s.empty}>No hay cargas registradas hoy</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={s.table}>
                <thead>
                  <tr>
                    {["Medidor", "Teléfonos", "Nombre", "Lectura", "Turno", "Naturgy", "Observación", "Hora", "Foto"].map((h) => (
                      <th key={h} style={s.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {history.map((row) => (
                    <tr key={row.id} style={s.tr}>
                      <td style={s.td}><strong>{row.medidor}</strong></td>
                      <td style={s.td}>
                        {(row.telefonos?.length ? row.telefonos : [row.telefono].filter(Boolean)).join(" / ") || "—"}
                      </td>
                      <td style={s.td}>{row.nombre_contacto || "—"}</td>
                      <td style={s.td}>{row.lectura        || "—"}</td>
                      <td style={s.td}>{row.turno          ?? "—"}</td>
                      <td style={s.td}>
                        <span style={row.enviado_naturgy ? s.chipYes : s.chipNo}>
                          {row.enviado_naturgy ? "Sí" : "No"}
                        </span>
                      </td>
                      <td style={{ ...s.td, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {row.observacion || "—"}
                      </td>
                      <td style={s.td}>{format(new Date(row.created_at), "HH:mm")}</td>
                      <td style={s.td}>
                        {(() => {
                          const urls = row.photo_urls?.length
                            ? row.photo_urls
                            : row.photo_url
                            ? [row.photo_url]
                            : [];
                          if (!urls.length) return "—";
                          return (
                            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                              {urls.slice(0, 3).map((u, i) => (
                                <a key={i} href={u} target="_blank" rel="noreferrer">
                                  <img src={u} alt="" style={s.thumb} />
                                </a>
                              ))}
                              {urls.length > 3 && (
                                <span style={{ fontSize: 11, color: "#94a3b8" }}>
                                  +{urls.length - 3}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s = {
  root: { minHeight: "100vh", background: "#f0f4f8" },
  header: {
    background: "#0f172a", display: "flex", alignItems: "center",
    justifyContent: "space-between", padding: "0 32px", height: 56,
    position: "sticky", top: 0, zIndex: 100,
  },
  headerLeft: { display: "flex", alignItems: "center", gap: 16 },
  logoutBtn: {
    display: "flex", alignItems: "center", background: "#1e293b",
    border: "none", borderRadius: 8, color: "#64748b", cursor: "pointer",
    fontSize: 12, fontWeight: 600, padding: "7px 14px", fontFamily: "inherit",
  },
  page: { padding: 32 },
  pageTitle: { fontSize: 26, fontWeight: 800, color: "#1a1a2e", margin: "0 0 24px" },

  // Padron card
  padronCard: {
    background: "#fff", borderRadius: 14, padding: "14px 20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)", marginBottom: 16,
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
  },
  padronCardLeft: { display: "flex", alignItems: "center", gap: 10 },
  padronTitle: { fontSize: 13, fontWeight: 700, color: "#1a1a2e" },
  padronStatusOk:    { fontSize: 12, color: "#16a34a", fontWeight: 600 },
  padronStatusEmpty: { fontSize: 12, color: "#94a3b8" },
  btnPadronImport: {
    display: "flex", alignItems: "center", gap: 5, padding: "7px 13px",
    borderRadius: 8, border: "1px solid #e2e8f0", background: "#f8fafc",
    color: "#475569", fontSize: 12, fontWeight: 600, fontFamily: "inherit", whiteSpace: "nowrap",
  },

  // Padron info
  padronInfoBlock: {
    marginTop: 12, background: "#f0fdf4", border: "1px solid #bbf7d0",
    borderRadius: 10, padding: "12px 14px",
  },
  padronInfoGrid:  { display: "flex", flexWrap: "wrap", gap: "6px 20px" },
  padronInfoCell:  { display: "flex", flexDirection: "column", gap: 1 },
  padronInfoLabel: { fontSize: 10, fontWeight: 700, color: "#15803d", textTransform: "uppercase", letterSpacing: "0.04em" },
  padronInfoVal:   { fontSize: 12, fontWeight: 600, color: "#166534" },

  // Main card
  card: {
    background: "#fff", borderRadius: 14, padding: "24px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)", marginBottom: 20,
  },
  sectionLabel: {
    fontSize: 11, fontWeight: 700, color: "#64748b",
    textTransform: "uppercase", letterSpacing: "0.05em", margin: "0 0 10px",
  },
  required: { color: "#ef4444", fontWeight: 600 },
  medidorRow: { display: "flex", gap: 10 },
  input: {
    padding: "10px 12px", borderRadius: 8, border: "1px solid #e2e8f0",
    fontSize: 13, color: "#1a1a2e", outline: "none", fontFamily: "inherit",
    boxSizing: "border-box", width: "100%", transition: "border-color .15s",
  },
  btnPrimary: {
    padding: "10px 18px", borderRadius: 9, border: "none", background: "#D4620D",
    color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 13,
    fontFamily: "inherit", whiteSpace: "nowrap", transition: "opacity .15s",
  },
  bannerFound: { marginTop: 10, padding: "9px 14px", borderRadius: 8, background: "#dcfce7", color: "#166534", fontSize: 13, fontWeight: 600 },
  bannerNew:   { marginTop: 10, padding: "9px 14px", borderRadius: 8, background: "#eff6ff", color: "#1d4ed8", fontSize: 13, fontWeight: 600 },
  addressBanner: {
    marginTop: 10, padding: "10px 14px", borderRadius: 8,
    background: "#fff7ed", border: "1px solid #fed7aa",
    display: "flex", flexDirection: "column", gap: 8,
  },
  addressBannerTitle: { fontSize: 13, fontWeight: 700, color: "#c2410c" },
  addressChips: { display: "flex", flexWrap: "wrap", gap: 6 },
  chipCurrent: {
    padding: "3px 10px", borderRadius: 6, fontSize: 12, fontWeight: 700,
    background: "#D4620D", color: "#fff",
  },
  chipOther: {
    padding: "3px 10px", borderRadius: 6, fontSize: 12, fontWeight: 600,
    background: "#fff", border: "1px solid #fed7aa", color: "#c2410c",
  },
  divider: { height: 1, background: "#f1f5f9", margin: "20px 0" },
  uploadArea: {
    display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
    width: "100%", padding: "28px 0", borderRadius: 10,
    border: "2px dashed #e2e8f0", background: "#f8fafc",
    cursor: "pointer", fontFamily: "inherit",
  },
  uploadText: { fontSize: 13, color: "#64748b", fontWeight: 600 },
  photoGrid: { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 },
  photoWrap: { position: "relative", display: "inline-block" },
  photoGridImg: { width: 90, height: 90, borderRadius: 10, objectFit: "cover", display: "block" },
  removeBtn: {
    position: "absolute", top: 4, right: 4, background: "rgba(0,0,0,0.55)",
    border: "none", borderRadius: "50%", width: 22, height: 22,
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", color: "#fff",
  },
  uploadAreaSmall: { padding: "10px 0", gap: 5 },
  lockedNote: {
    fontSize: 12, color: "#94a3b8", fontWeight: 600, background: "#f8fafc",
    borderRadius: 8, padding: "8px 12px", marginBottom: 14, border: "1px solid #f1f5f9",
  },
  grid3: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 16px", marginBottom: 4 },
  fieldLabel: {
    display: "block", fontSize: 11, fontWeight: 700, color: "#64748b",
    marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em",
  },
  checkRow:  { display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none", marginTop: 14 },
  checkLabel: { fontSize: 13, fontWeight: 600, color: "#1a1a2e" },
  textarea: {
    width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #e2e8f0",
    fontSize: 13, fontFamily: "inherit", resize: "vertical",
    boxSizing: "border-box", outline: "none", color: "#1a1a2e",
  },
  autoGrid: {
    display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 1,
    background: "#f1f5f9", borderRadius: 10, overflow: "hidden", marginTop: 16,
  },
  autoCell:      { background: "#fff", padding: "11px 14px", display: "flex", flexDirection: "column", gap: 3 },
  autoCellLabel: { fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" },
  autoCellVal:   { fontSize: 13, fontWeight: 600, color: "#475569" },
  errorBox:   { background: "#fee2e2", color: "#dc2626", borderRadius: 9, padding: "10px 14px", fontSize: 13, fontWeight: 600, marginTop: 12 },
  successBox: { background: "#dcfce7", color: "#16a34a", borderRadius: 9, padding: "10px 14px", fontSize: 13, fontWeight: 600, marginTop: 12 },
  btnSave: {
    width: "100%", padding: "13px", borderRadius: 10, border: "none",
    background: "#D4620D", color: "#fff", fontWeight: 700, fontSize: 15,
    fontFamily: "inherit", transition: "opacity .15s",
  },
  historyHeader: { marginBottom: 16 },
  historyTitle: {
    fontSize: 16, fontWeight: 800, color: "#1a1a2e", margin: 0,
    display: "flex", alignItems: "center", gap: 10,
  },
  historyBadge: { background: "#fff7ed", color: "#D4620D", borderRadius: 8, padding: "2px 10px", fontSize: 13, fontWeight: 700 },
  empty: { textAlign: "center", padding: "32px 0", color: "#94a3b8", fontSize: 14 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    padding: "11px 14px", textAlign: "left", fontSize: 11, fontWeight: 700,
    color: "#94a3b8", borderBottom: "1px solid #f1f5f9",
    textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap",
  },
  tr: { borderBottom: "1px solid #f8fafc" },
  td: { padding: "11px 14px", color: "#1e293b", verticalAlign: "middle" },
  chipYes: { background: "#fff7ed", color: "#D4620D", borderRadius: 6, padding: "2px 9px", fontSize: 11, fontWeight: 700 },
  chipNo:  { background: "#f1f5f9", color: "#64748b", borderRadius: 6, padding: "2px 9px", fontSize: 11, fontWeight: 700 },
  thumb:   { width: 40, height: 40, borderRadius: 7, objectFit: "cover", display: "block" },
  addPhoneBtn: {
    background: "none", border: "1px dashed #94a3b8", borderRadius: 8,
    color: "#64748b", fontSize: 12, fontWeight: 600, padding: "5px 12px",
    cursor: "pointer", marginTop: 2,
  },
  removePhoneBtn: {
    background: "#fee2e2", border: "none", borderRadius: 8,
    color: "#dc2626", fontSize: 16, fontWeight: 700, width: 34, flexShrink: 0,
    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
  },
};
